import React from 'react';
import { Navigate } from 'react-router-dom';
import { z } from 'zod';
import {
  Typography,
  FlexBox,
  AbsoluteBox,
  SvgIconButton,
  Grid,
  TextField,
  MuiBox,
  textSize,
  color,
  SvgIcon
} from '@kis-technologies/kis-ui';
import { Keyboard } from 'components/Keyboard';
import { useActions } from 'hooks/useActions';
import { useSelector } from 'react-redux';
import { selectors, actions } from 'services/auth';
import { actions as salesErrorActions } from 'services/salesErrors';
import { handleInputChange } from 'utils/helpers';
import { useStyles } from './styled';

const schema = z.email('Invalid email format');

const SignInView: React.FC = () => {
  // Action Creators and Selectors
  const { adminLogin, resetSalesError, resetAdminMessage } = useActions({
    adminLogin: actions.adminLogin,
    resetSalesError: salesErrorActions.resetError,
    resetAdminMessage: actions.resetAdminMessage
  });
  const loading = useSelector(selectors.selectLoading);
  const adminToken = useSelector(selectors.selectAdminId);
  const error = useSelector(selectors.selectAdminError);
  const classes = useStyles();

  // Set Email Invalid Message
  const [emailInvalid, setEmailInvalid] = React.useState<string | null>(null);

  // Set Input Values
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');

  // Password or Email Inputs Selected
  const [input, setInput] = React.useState<string | null>(null);

  const inputEmailRef = React.useRef<HTMLInputElement>(null);
  const inputPasswordRef = React.useRef<HTMLInputElement>(null);

  // Callback Function to fire if email is not the same
  const validate = React.useCallback(() => {
    emailValidation(email);
  }, [email]);

  React.useEffect(() => {
    if (email && input !== 'email') validate();
    resetAdminMessage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, validate]);

  // ***************
  // ** FUNCTIONS **
  // ***************

  // Validate that the email is in correct format
  const emailValidation = (value: string) => {
    const result = schema.safeParse(value);
    setEmailInvalid(result.success ? null : result.error.issues[0]?.message || null);
  };

  const onKeyboardClick = (_event: React.MouseEvent<HTMLButtonElement>, value: string) => {
    if (input === 'email') {
      handleInputChange(value, setEmail, inputEmailRef);
      setEmailInvalid(null);
    } else {
      handleInputChange(value, setPassword, inputPasswordRef);
    }

    if (error) resetAdminMessage();
  };

  // Handle which input ref has the focus
  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setInput(event.currentTarget.name);
  };

  // Handle the submission of the form
  const handleSubmit = () => {
    resetSalesError();
    adminLogin({ email, password });
  };

  // Hides the clear button if the input is blank
  const userNameOpacity = email.length < 1 ? 0 : 1;
  const userNameClearPointer = email.length < 1 ? 'none' : 'auto';
  const passwordOpacity = password.length < 1 ? 0 : 1;
  const passwordClearPointer = password.length < 1 ? 'none' : 'auto';
  const transformTransition = 'opacity 250ms linear';

  const submitDisabled = !email.length || !password.length || !schema.safeParse(email).success;

  if (adminToken) return <Navigate to="/systems" />;

  return (
    <>
      <FlexBox noWrap fullWidth direction="column" align="center" justify="center" padding="0 5">
        <Grid className={classes.signinViewOuterContainer}>
          <Grid container className={classes.signinViewInnerContainer} spacing={2}>
            {/* Hack to fix the autofill setting focus to password field */}
            <input type="email" name="hidden" id="hidden" style={{ width: 0, height: 0, border: 0, padding: 0 }} />
            <input type="password" name="hidden" id="hidden" style={{ width: 0, height: 0, border: 0, padding: 0 }} />

            <Grid item md={12} xl={12}>
              <div className={classes.signinViewTextBlock}>
                <Typography className={classes.signinViewText}>Enter Your Email &amp; Password</Typography>
              </div>
            </Grid>
            <Grid item className={classes.signinViewInputEmailBlock} md={10} xl={5} lg={5}>
              <FlexBox margin="0 0 0" height={150} width="100%">
                <TextField
                  error={emailInvalid && emailInvalid.length > 0 ? true : false}
                  variant="outlined"
                  autoFocus
                  fullWidth
                  className={classes.muiTextField}
                  focused={input === 'email'}
                  value={email}
                  inputRef={inputEmailRef}
                  onFocus={handleFocus}
                  helperText={emailInvalid}
                  FormHelperTextProps={{
                    classes: {
                      root: classes.helpText
                    }
                  }}
                  InputProps={{
                    placeholder: 'Email',
                    classes: {
                      root: classes.input
                    },
                    endAdornment: (
                      <SvgIconButton
                        sx={{
                          boxShadow: 'none',
                          opacity: userNameOpacity,
                          pointerEvents: userNameClearPointer,
                          marginLeft: '20px'
                        }}
                        customTransition={transformTransition}
                        appearance="transparent"
                        onClick={() => {
                          setEmail('');
                          setEmailInvalid(null);
                          resetAdminMessage();
                        }}
                      >
                        <SvgIcon name="exit_circle" size={40} />
                      </SvgIconButton>
                    ),
                    inputProps: {
                      name: 'email',
                      align: 'center',
                      style: { textAlign: 'center' }
                    }
                  }}
                />
              </FlexBox>
            </Grid>
            <Grid className={classes.signinViewInputPasswordBlock} item md={10} xl={5} lg={5}>
              <FlexBox margin="0 0" height={150} width="100%">
                <TextField
                  variant="outlined"
                  type="password"
                  fullWidth
                  className={classes.muiTextField}
                  focused={input === 'password'}
                  value={password}
                  inputRef={inputPasswordRef}
                  onFocus={handleFocus}
                  InputProps={{
                    placeholder: 'Password',
                    classes: {
                      root: classes.input
                    },
                    endAdornment: (
                      <SvgIconButton
                        sx={{
                          boxShadow: 'none',
                          opacity: passwordOpacity,
                          pointerEvents: passwordClearPointer
                        }}
                        customTransition={transformTransition}
                        appearance="transparent"
                        onClick={() => {
                          setPassword('');
                          resetAdminMessage();
                        }}
                      >
                        <SvgIcon name="exit_circle" size={40} />
                      </SvgIconButton>
                    ),
                    inputProps: {
                      name: 'password',
                      align: 'center',
                      style: { textAlign: 'center' }
                    }
                  }}
                />
              </FlexBox>
            </Grid>
            <Grid item md={10} xl={5}>
              {!emailInvalid && error && (
                <Typography align="center" color={color.RED} fontSize={textSize.kiosk.SMALL}>
                  {error}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Grid>
      </FlexBox>
      <MuiBox className={classes.signinKeyboardBox}>
        <AbsoluteBox raised fullWidth bg="x-light-gray" height="480px" bottom="0" left="0">
          <FlexBox fullHeight align="center" direction="column">
            <Keyboard
              onClick={onKeyboardClick}
              onNext={handleSubmit}
              isLoading={loading}
              isNextDisabled={submitDisabled}
              nextText="Sign In"
              suggestions={[
                { value: '@gmail.com', display: '@gmail.com' },
                { value: '@yahoo.com', display: '@yahoo.com' }
              ]}
              onSuggestionClick={onKeyboardClick}
            />
          </FlexBox>
        </AbsoluteBox>
      </MuiBox>
    </>
  );
};

export default SignInView;
